import React from "react";
import { BookOpen } from "lucide-react";
import { TierBadge } from "./useMcpCatalog";

const h2 = { margin: 0, fontSize: 22, fontWeight: 700, letterSpacing: "-0.02em", color: "#191c1d", fontFamily: "'Reckless Neue Variable', serif" };
const mono = { fontFamily: "ui-monospace, monospace" };

const fmtType = (p) => {
  if (p.enum) return p.enum.join(" | ");
  let t = p.type;
  if (p.minimum !== undefined && p.maximum !== undefined) t += ` ${p.minimum}–${p.maximum}`;
  else if (p.minimum !== undefined) t += ` ≥ ${p.minimum}`;
  else if (p.maximum !== undefined) t += ` ≤ ${p.maximum}`;
  if (p.minLength !== undefined && p.minLength === p.maxLength) t += ` (${p.minLength} chars)`;
  if (p.pattern) t += ` (${p.pattern.replace(/\\\\/g, "\\")})`;
  return t;
};

function ParamTable({ params, name }) {
  if (!params?.length) return <p style={{ margin: "10px 0 0", fontSize: 12, color: "#6b7a7d" }}>No parameters.</p>;
  return (
    <table data-testid={`mcp-tool-params-${name}`} style={{ width: "100%", borderCollapse: "collapse", marginTop: 10, fontSize: 12 }}>
      <thead><tr style={{ textAlign: "left", color: "#6b7a7d" }}>{["Parameter", "Type / values", "Description"].map((h) => <th key={h} style={{ padding: "4px 8px 6px 0", fontWeight: 600, borderBottom: "1px solid #e8ecee" }}>{h}</th>)}</tr></thead>
      <tbody>
        {params.map((p) => (
          <tr key={p.name} style={{ verticalAlign: "top" }}>
            <td style={{ padding: "6px 8px 6px 0", whiteSpace: "nowrap" }}><code style={{ ...mono, color: "#191c1d", fontWeight: 700 }}>{p.name}</code>{p.required && <span style={{ color: "#b91c1c", marginLeft: 4 }} title="required">*</span>}</td>
            <td style={{ padding: "6px 8px 6px 0", color: "#3b494c", overflowWrap: "anywhere" }}><code style={{ ...mono, fontSize: 11, overflowWrap: "anywhere" }}>{fmtType(p)}</code>{p.default && <span style={{ color: "#6b7a7d" }}> · default {p.default}</span>}</td>
            <td style={{ padding: "6px 0", color: "#3b494c", lineHeight: 1.5 }}>{p.description}</td>
          </tr>
        ))}
      </tbody>
    </table>
  );
}

function ToolCard({ tool, maxResults }) {
  const pro = tool.tier === "pro";
  return (
    <article data-testid={`mcp-tool-${tool.name}`} style={{ background: pro ? "#faf5ff" : "#f8f9fa", border: `1px solid ${pro ? "#e9d5ff" : "#e8ecee"}`, borderRadius: 14, padding: "16px 18px" }}>
      <div style={{ display: "flex", gap: 10, alignItems: "center", flexWrap: "wrap" }}>
        <code style={{ ...mono, fontSize: 14, fontWeight: 700, color: pro ? "#7c3aed" : "#006874" }}>{tool.name}</code>
        <TierBadge tier={tool.tier} />
        {tool.read_only && <span style={{ fontSize: 10, color: "#6b7a7d", background: "#eef1f2", borderRadius: 999, padding: "2px 8px" }}>read-only</span>}
      </div>
      <p style={{ margin: "8px 0 0", fontSize: 13, color: "#3b494c", lineHeight: 1.65 }}>{tool.description}</p>
      <ParamTable params={tool.parameters} name={tool.name} />
      {tool.returns && (
        <p data-testid={`mcp-tool-returns-${tool.name}`} style={{ margin: "10px 0 0", fontSize: 12, color: "#3b494c", lineHeight: 1.6 }}>
          <strong>Returns</strong> <code style={{ ...mono, fontSize: 11 }}>{tool.returns.root}</code>: {tool.returns.fields.join(" · ")}
          {tool.returns.extra && <span style={{ color: "#6b7a7d" }}> — plus {tool.returns.extra.join(", ")}</span>}
          {tool.returns.root.endsWith("[]") && <span style={{ color: "#6b7a7d" }}> · max {maxResults} per call</span>}
        </p>
      )}
      {tool.example_prompt && (
        <p data-testid={`mcp-tool-example-${tool.name}`} style={{ margin: "10px 0 0", fontSize: 12, color: "#6b7a7d", fontStyle: "italic", lineHeight: 1.6 }}>
          Try in Claude / Cursor: “{tool.example_prompt}”
        </p>
      )}
    </article>
  );
}

export default function McpToolReference({ catalog, live }) {
  const { domains = [], tools = [], max_results: maxResults = 20, counts = {} } = catalog || {};
  return (
    <section id="tools" style={{ background: "#fff", padding: "48px 24px", borderTop: "1px solid #e8ecee", borderBottom: "1px solid #e8ecee" }}>
      <div style={{ maxWidth: 860, margin: "0 auto" }}>
        <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 6 }}>
          <BookOpen style={{ width: 18, height: 18, color: "#006874" }} />
          <h2 style={h2}>Tool reference</h2>
        </div>
        <p data-testid="mcp-tools-source" style={{ margin: "0 0 22px", fontSize: 13, color: "#6b7a7d", lineHeight: 1.6 }}>
          {counts.open} Open tools + {counts.pro - counts.open} Pro tool, {live ? "read live from the server" : "static copy"} — every parameter and return field below is what the server exposes today.
        </p>
        <div data-testid="mcp-tools-table" style={{ display: "flex", flexDirection: "column", gap: 28 }}>
          {domains.map((d) => {
            const list = tools.filter((t) => t.domain === d.key);
            if (!list.length) return null;
            return (
              <div key={d.key} data-testid={`mcp-domain-${d.key}`}>
                <h3 style={{ margin: 0, fontSize: 16, fontWeight: 700, color: "#191c1d" }}>{d.label}</h3>
                <p style={{ margin: "4px 0 12px", fontSize: 13, color: "#6b7a7d" }}>{d.blurb}</p>
                <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>{list.map((t) => <ToolCard key={t.name} tool={t} maxResults={maxResults} />)}</div>
              </div>
            );
          })}
        </div>
      </div>
    </section>
  );
}
